import { supabase } from "@/lib/supabase";
import type { Database } from "@/integrations/supabase/types";
import type { EquipmentAsset, EquipmentAssetCategory, EquipmentProtocol, StaffEquipmentItem } from "@/types/equipment";
import { EquipmentApiError, isMissingEquipmentRelation } from "@/lib/equipmentMessages";
import { toEquipmentError, validateConditionNotes } from "@/lib/equipmentStorage";

type StaffEquipmentRow = Database["public"]["Tables"]["staff_equipment"]["Row"];

const ASSET_CATEGORIES: EquipmentAssetCategory[] = ["tool", "other"];
const ASSET_STATUSES: EquipmentAsset["status"][] = [
  "available",
  "pending_handover",
  "assigned",
  "pending_return",
  "retired",
];
const PROTOCOL_KINDS: EquipmentProtocol["kind"][] = ["asset", "key_card"];
const PROTOCOL_DIRECTIONS: EquipmentProtocol["direction"][] = ["handover", "return"];
const PROTOCOL_STATUSES: EquipmentProtocol["status"][] = ["pending", "accepted", "rejected", "cancelled"];
const STAFF_TYPES: StaffEquipmentItem["type"][] = ["key", "card", "other"];
const STAFF_STATUSES: StaffEquipmentItem["status"][] = [
  "pending_handover",
  "assigned",
  "pending_return",
  "returned",
];

function isOneOf<T extends string>(value: string, allowed: readonly T[]): value is T {
  return (allowed as readonly string[]).includes(value);
}

type EquipmentProtocolLike = {
  id: string;
  org_id: string;
  kind: string;
  asset_id: string | null;
  staff_equipment_id: string | null;
  worker_id: string;
  direction: string;
  status: string;
  initiated_by: string;
  responded_by: string | null;
  condition_notes: string | null;
  photo_urls: string[];
  rejection_reason: string | null;
  initiated_at: string;
  responded_at: string | null;
  created_at: string;
};

type EquipmentAssetLike = {
  id: string;
  org_id: string;
  name: string;
  category: string;
  serial_number: string | null;
  status: string;
  current_holder_id: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export function toEquipmentAsset(row: EquipmentAssetLike): EquipmentAsset {
  return {
    ...row,
    category: isOneOf(row.category, ASSET_CATEGORIES) ? row.category : "tool",
    status: isOneOf(row.status, ASSET_STATUSES) ? row.status : "available",
  };
}

export function toEquipmentProtocol(row: EquipmentProtocolLike): EquipmentProtocol {
  return {
    ...row,
    kind: isOneOf(row.kind, PROTOCOL_KINDS) ? row.kind : "asset",
    direction: isOneOf(row.direction, PROTOCOL_DIRECTIONS) ? row.direction : "handover",
    status: isOneOf(row.status, PROTOCOL_STATUSES) ? row.status : "pending",
  };
}

function toStaffEquipmentItem(row: StaffEquipmentRow): StaffEquipmentItem {
  return {
    ...row,
    type: isOneOf(row.type, STAFF_TYPES) ? row.type : "other",
    status: isOneOf(row.status, STAFF_STATUSES) ? row.status : "assigned",
  };
}

export async function listEquipmentAssets(orgId: string): Promise<EquipmentAsset[]> {
  if (!orgId) throw new EquipmentApiError("EQUIPMENT_ORG_REQUIRED");
  const { data, error } = await supabase
    .from("equipment_assets")
    .select(
      "id, org_id, name, category, serial_number, status, current_holder_id, notes, created_by, created_at, updated_at",
    )
    .eq("org_id", orgId)
    .order("name", { ascending: true });
  if (error) {
    if (isMissingEquipmentRelation(error)) {
      console.warn("[equipmentApi:listEquipmentAssets] schema not ready", error);
      return [];
    }
    throw toEquipmentError(error, "listEquipmentAssets");
  }
  return (data ?? []).map(toEquipmentAsset);
}

export async function listStaffEquipment(
  orgId: string,
  workerId: string,
): Promise<StaffEquipmentItem[]> {
  if (!orgId) throw new EquipmentApiError("EQUIPMENT_ORG_REQUIRED");
  if (!workerId) throw new EquipmentApiError("EQUIPMENT_WORKER_REQUIRED");
  const { data, error } = await supabase
    .from("staff_equipment")
    .select("id, org_id, staff_id, type, name, status, assigned_at, returned_at, created_by, created_at")
    .eq("org_id", orgId)
    .eq("staff_id", workerId)
    .order("assigned_at", { ascending: false });
  if (error) {
    if (isMissingEquipmentRelation(error)) {
      console.warn("[equipmentApi:listStaffEquipment] schema not ready", error);
      return [];
    }
    throw toEquipmentError(error, "listStaffEquipment");
  }
  return (data ?? []).map(toStaffEquipmentItem);
}

export async function listEquipmentProtocols(params: {
  orgId: string;
  workerId?: string;
  assetId?: string;
  staffEquipmentId?: string;
  pendingOnly?: boolean;
}): Promise<EquipmentProtocol[]> {
  if (!params.orgId) throw new EquipmentApiError("EQUIPMENT_ORG_REQUIRED");
  let query = supabase
    .from("equipment_protocols")
    .select(
      "id, org_id, kind, asset_id, staff_equipment_id, worker_id, direction, status, initiated_by, responded_by, condition_notes, photo_urls, rejection_reason, initiated_at, responded_at, created_at",
    )
    .eq("org_id", params.orgId)
    .order("initiated_at", { ascending: false });

  if (params.workerId) query = query.eq("worker_id", params.workerId);
  if (params.assetId) query = query.eq("asset_id", params.assetId);
  if (params.staffEquipmentId) query = query.eq("staff_equipment_id", params.staffEquipmentId);
  if (params.pendingOnly) query = query.eq("status", "pending");

  const { data, error } = await query;
  if (error) {
    if (isMissingEquipmentRelation(error)) {
      console.warn("[equipmentApi:listEquipmentProtocols] schema not ready", error);
      return [];
    }
    throw toEquipmentError(error, "listEquipmentProtocols");
  }
  return (data ?? []).map(toEquipmentProtocol);
}

export async function createEquipmentAsset(input: {
  orgId: string;
  name: string;
  category?: EquipmentAssetCategory;
  serialNumber?: string | null;
  notes?: string | null;
  createdBy?: string | null;
}): Promise<EquipmentAsset> {
  if (!input.orgId) throw new EquipmentApiError("EQUIPMENT_ORG_REQUIRED");
  const name = input.name.trim();
  if (!name) throw new EquipmentApiError("EQUIPMENT_NAME_REQUIRED");
  const serial = input.serialNumber?.trim() || null;

  const { data, error } = await supabase
    .from("equipment_assets")
    .insert({
      org_id: input.orgId,
      name,
      category: input.category ?? "tool",
      serial_number: serial,
      notes: validateConditionNotes(input.notes),
      created_by: input.createdBy ?? null,
    })
    .select(
      "id, org_id, name, category, serial_number, status, current_holder_id, notes, created_by, created_at, updated_at",
    )
    .single();

  if (error) throw toEquipmentError(error, "createEquipmentAsset");
  return toEquipmentAsset(data);
}

export async function updateEquipmentAsset(input: {
  id: string;
  name: string;
  serialNumber?: string | null;
  notes?: string | null;
}): Promise<EquipmentAsset> {
  if (!input.id) throw new EquipmentApiError("EQUIPMENT_NOT_FOUND");
  const name = input.name.trim();
  if (!name) throw new EquipmentApiError("EQUIPMENT_NAME_REQUIRED");
  const serial = input.serialNumber?.trim() || null;

  const { data, error } = await supabase
    .from("equipment_assets")
    .update({
      name,
      serial_number: serial,
      notes: validateConditionNotes(input.notes),
    })
    .eq("id", input.id)
    .select(
      "id, org_id, name, category, serial_number, status, current_holder_id, notes, created_by, created_at, updated_at",
    )
    .single();

  if (error) throw toEquipmentError(error, "updateEquipmentAsset");
  return toEquipmentAsset(data);
}
